import { useSelector } from 'react-redux';
import type { Seconds } from '@ankr.com/utils/unit';

import { useGetIsSafeAccountQuery } from 'modules/api/actions/getIsSafeAccount';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';

import { DEFAULT_CONNECTION_KEY, EConnectionType } from '../const';
import {
  IConnectionStateItem,
  selectConnection,
  selectConnectionType,
} from '../store/connectionSlice';

export interface IConnectionData
  extends Omit<IConnectionStateItem, 'key' | 'type'> {
  connectionKey: string;
  connectionType: EConnectionType | null;
  walletIcon: string | null;
  isSafeAccount: boolean;
  cacheTime: Seconds;
}

export const ACTION_CACHE_SHORT: Seconds = 20;
export const ACTION_CACHE: Seconds = 60;

export function useConnection(): IConnectionData {
  const {
    key: storedConnectionKey,
    chainId,
    accountAddress,
    isConnected,
    disconnect,
    walletIcon,
  } = useSelector(selectConnection);
  const connectionType = useSelector(selectConnectionType);
  const connectionKey = isConnected
    ? storedConnectionKey
    : DEFAULT_CONNECTION_KEY;

  const handledIsConnected =
    DEFAULT_CONNECTION_KEY !== connectionKey && isConnected;

  const { data: isSafeAccount = false } = useGetIsSafeAccountQuery(
    { connectionKey },
    {
      skip: !handledIsConnected || !accountAddress,
      selectFromResult: mapDataToUndefinedIfSkip,
    },
  );

  return {
    chainId,
    accountAddress,
    isConnected: handledIsConnected,
    connectionKey,
    connectionType,
    walletIcon,
    disconnect,
    isSafeAccount,
    cacheTime: isSafeAccount ? ACTION_CACHE_SHORT : ACTION_CACHE,
  };
}
